import { useMemo } from 'react';
import { useFormFactor } from '@/lib/formFactor';
import { useSession } from '@/stores/session';
import { ForbiddenModule } from '@/shell/AccessResult';
import { Dot } from '@/components/Tag';
import { Icon } from '@/components/Icon';
import { ErrorState, Skeleton } from '@/components/States';
import { money, qty, shortDate } from '@/lib/format';
import { Chips, DataTable, DeskHead, PhoneBar, Segmented, Tile, Tiles, type Col } from '../admin/kit/Kit';
import { useApi, useUrlState } from '../admin/kit/hooks';
import { purchaseBasis, salesBasis, turnoverStatus } from './status';
import type { AgingBucket, BusinessReport, InventoryTurnoverRow, PartnerRank, TurnoverStatus } from './types';
import s from './ReportsPage.module.css';

const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const ym = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;

/* ------------------------------ 账龄：有序分档 → 单色横条（dataviz：一个系列一种颜色，值写在条尾） ------------------------------ */
function AgingBars({ title, rows, asOf }: { title: string; rows: AgingBucket[]; asOf: string }) {
  const max = Math.max(...rows.map((r) => r.amount), 0);
  const total = rows.reduce((a, r) => a + r.amount, 0);
  const overdue = rows.filter((r) => r.bucket !== '未到期').reduce((a, r) => a + r.amount, 0);
  return (
    <section className={s.panel}>
      <header className={s.ph}>
        <h3>{title}<small>截至 {shortDate(asOf)}</small></h3>
        <span className={s.phx}>合计 <b className="tnum">{money(total)}</b></span>
      </header>
      <ul className={s.bars} aria-label={title}>
        {rows.map((r) => (
          <li key={r.bucket} title={`${r.bucket}：${money(r.amount)}，${r.count} 单`}>
            <span className={s.bl}>{r.bucket}</span>
            <span className={s.track}>{r.amount > 0 && <i style={{ width: `${Math.max((r.amount / (max || 1)) * 100, 1.5)}%` }} />}</span>
            <span className={s.bv}><b className="tnum">{money(r.amount)}</b><small className="tnum">{r.count} 单</small></span>
          </li>
        ))}
      </ul>
      {total > 0 && <p className={s.pfoot}>已逾期 <b className={`tnum ${overdue > 0 ? s.bad : ''}`}>{money(overdue)}</b>，占 {Math.round((overdue / total) * 100)}%</p>}
    </section>
  );
}

/* ------------------------------ 往来单位排行 ------------------------------ */
function Ranking({ title, rows, kind, phone }: { title: string; rows: PartnerRank[]; kind: 'customer' | 'supplier'; phone: boolean }) {
  const top = rows.slice(0, 10);
  const max = Math.max(...top.map((r) => r.amount), 0);
  const paid = kind === 'customer' ? '已收' : '已付';
  const unpaid = kind === 'customer' ? '未收' : '未付';
  const inv = kind === 'customer' ? '未开票' : '未收票';
  const bar = (r: PartnerRank) => <span className={s.track}>{r.amount > 0 && <i style={{ width: `${Math.max((r.amount / (max || 1)) * 100, 1.5)}%` }} />}</span>;
  const cols: Col<PartnerRank>[] = [
    { key: 'i', title: '#', w: 40, render: (r) => <span className="tnum em">{top.indexOf(r) + 1}</span> },
    { key: 'n', title: kind === 'customer' ? '客户' : '供应商', w: 200, render: (r) => <span title={r.name}>{r.name}</span> },
    { key: 'a', title: '业务金额', w: 220, render: (r) => <span className={s.amtCell}>{bar(r)}<b className="tnum">{money(r.amount)}</b></span> },
    { key: 'c', title: paid, w: 120, align: 'r', render: (r) => money(r.cashAmount) },
    { key: 'o', title: unpaid, w: 120, align: 'r', render: (r) => <span className={r.outstandingAmount > 0 ? s.warnT : 'em'}>{money(r.outstandingAmount)}</span> },
    { key: 'v', title: inv, w: 120, align: 'r', render: (r) => money(r.invoiceBalanceAmount) },
    { key: 'k', title: '单据', w: 60, align: 'r', render: (r) => r.count },
  ];
  return (
    <section className={s.panel}>
      <header className={s.ph}>
        <h3>{title}<small>按期间业务金额{rows.length > 10 ? ` · 前 10 / 共 ${rows.length}` : ''}</small></h3>
      </header>
      {!top.length ? (
        <p className={s.none}>期间内没有{kind === 'customer' ? '销售' : '采购'}单据。</p>
      ) : phone ? (
        <ol className={s.rank}>
          {top.map((r, i) => (
            <li key={r.id}>
              <div className={s.rh}><i className="tnum">{i + 1}</i><b>{r.name}</b><span className="tnum">{money(r.amount)}</span></div>
              {bar(r)}
              <p className="tnum">{paid} {money(r.cashAmount)} · <span className={r.outstandingAmount > 0 ? s.warnT : ''}>{unpaid} {money(r.outstandingAmount)}</span> · {r.count} 单</p>
            </li>
          ))}
        </ol>
      ) : (
        <DataTable cols={cols} rows={top} rowKey={(r) => r.id} dense />
      )}
    </section>
  );
}

/* ------------------------------ 库存周转 ------------------------------ */
function Turnover({ rows, days, phone, filter, onFilter }: { rows: InventoryTurnoverRow[]; days: number; phone: boolean; filter: string; onFilter: (k: string) => void }) {
  const counts = (st: TurnoverStatus) => rows.filter((r) => r.turnoverStatus === st).length;
  const list = filter === 'all' ? rows : rows.filter((r) => r.turnoverStatus === filter);
  const chips = (
    <Chips value={filter} onChange={onFilter}
      items={[{ key: 'all', label: '全部', count: rows.length }, ...(['slow', 'no-outbound', 'normal', 'fast'] as TurnoverStatus[]).map((k) => ({ key: k, label: turnoverStatus[k].label, count: counts(k) }))]} />
  );
  const cols: Col<InventoryTurnoverRow>[] = [
    { key: 'p', title: '产品', w: 220, fix: true, render: (r) => <div className={s.cell2}><b>{r.name}</b><small className="tnum">{r.code}</small></div> },
    { key: 'cat', title: '分类', w: 100, render: (r) => r.category || <span className="em">—</span> },
    { key: 'cur', title: '现有库存', w: 90, align: 'r', render: (r) => qty(r.currentQuantity) },
    { key: 'av', title: '可用库存', w: 90, align: 'r', render: (r) => <span className={r.availableQuantity < 0 ? 'neg' : undefined}>{qty(r.availableQuantity)}</span> },
    { key: 'rr', title: '预占率', w: 80, align: 'r', render: (r) => `${(r.reservedRate * 100).toFixed(1)}%` },
    { key: 'out', title: `近 ${days} 天出库`, w: 110, align: 'r', render: (r) => qty(r.outboundQuantity) },
    { key: 'avg', title: '日均出库', w: 90, align: 'r', render: (r) => qty(r.averageDailyOutbound) },
    { key: 'td', title: '周转天数', w: 90, align: 'r', render: (r) => (r.turnoverDays == null ? <span className="em">—</span> : `${qty(r.turnoverDays)} 天`) },
    { key: 'st', title: '周转状态', w: 96, render: (r) => <Dot tone={turnoverStatus[r.turnoverStatus].dot}>{turnoverStatus[r.turnoverStatus].label}</Dot> },
    { key: 'v', title: '库存价值', w: 120, align: 'r', render: (r) => money(r.stockValue) },
  ];
  return (
    <section className={s.panel}>
      <header className={s.ph}>
        <h3>库存周转<small>周转天数 = 现有库存 ÷ 近 {days} 天日均出库 · ≤30 天高周转，&gt;90 天偏慢</small></h3>
      </header>
      <div className={s.tchips}>{chips}</div>
      {!list.length ? (
        <p className={s.none}>没有这一档的产品。</p>
      ) : phone ? (
        <ul className={s.inv}>
          {list.map((r) => (
            <li key={r.productId}>
              <div className={s.ih}><b>{r.name}</b><Dot tone={turnoverStatus[r.turnoverStatus].dot}>{turnoverStatus[r.turnoverStatus].label}</Dot></div>
              <p className={s.isub}><span className="tnum">{r.code}</span>{r.category ? ` · ${r.category}` : ''}</p>
              <div className={s.inums}>
                <span><i>现有</i><b className="tnum">{qty(r.currentQuantity)}</b></span>
                <span><i>可用</i><b className={`tnum ${r.availableQuantity < 0 ? 'neg' : ''}`}>{qty(r.availableQuantity)}</b></span>
                <span><i>近{days}天出库</i><b className="tnum">{qty(r.outboundQuantity)}</b></span>
                <span><i>周转</i><b className="tnum">{r.turnoverDays == null ? '—' : `${qty(r.turnoverDays)} 天`}</b></span>
              </div>
              <p className={s.ival}>库存价值 <b className="tnum">{money(r.stockValue)}</b></p>
            </li>
          ))}
        </ul>
      ) : (
        <DataTable cols={cols} rows={list} rowKey={(r) => r.productId} dense />
      )}
    </section>
  );
}

/**
 * 经营报表（老系统 /reports 的只读新版）。
 * 形式按 dataviz 的克制原则：头条数字用瓦片；账龄是有序分档 → 单色横条、值写在条尾；排行和周转是表格（手机是列表）。
 * 口径照老接口，不自己算：期间、周转天数、账龄截止日都作为查询参数交给服务端。
 */
export function ReportsPage() {
  const ff = useFormFactor();
  const can = useSession((st) => st.can);
  const [sp, set] = useUrlState();
  const phone = ff === 'phone';
  const now = new Date();
  const mode = sp.get('p') === 'year' ? 'year' : 'month';
  const month = /^\d{4}-\d{2}$/.test(sp.get('m') || '') ? sp.get('m')! : ym(now);
  const year = /^\d{4}$/.test(sp.get('y') || '') ? sp.get('y')! : String(now.getFullYear());
  const days = [30, 60, 90].includes(Number(sp.get('days'))) ? Number(sp.get('days')) : 30;
  const sb = salesBasis.some((x) => x.value === sp.get('sb')) ? sp.get('sb')! : 'createdAt';
  const pb = purchaseBasis.some((x) => x.value === sp.get('pb')) ? sp.get('pb')! : 'orderDate';
  const invFilter = sp.get('inv') || 'all';
  // 账龄截止日：与老版一致，默认今天（老接口不传则取期间末）
  const asOf = ymd(now);
  const query = useMemo(
    () => ({ ...(mode === 'month' ? { month } : { year }), inventoryDays: days, agingAsOf: asOf, salesDateBasis: sb, purchaseDateBasis: pb }),
    [mode, month, year, days, asOf, sb, pb],
  );
  const res = useApi<BusinessReport>(can('reports.business.read') ? '/api/reports/business' : null, query);

  if (!can('reports.business.read')) return <ForbiddenModule moduleName="经营报表" description="这个链接打开的是应收账龄报表，" permissionName="查看经营报表" permissionCode="reports.business.read" />;

  const [yy, mm] = month.split('-').map(Number);
  const periodLabel = mode === 'month' ? `${yy} 年 ${mm} 月` : `${year} 年`;
  const isCurrent = mode === 'month' ? month === ym(now) : Number(year) === now.getFullYear();
  const step = (d: number) => {
    if (mode === 'month') set({ m: ym(new Date(yy, mm - 1 + d, 1)) });
    else set({ y: String(Number(year) + d) });
  };

  const controls = (
    <div className={s.controls}>
      <Segmented value={mode} onChange={(k) => set({ p: k === 'month' ? undefined : k })} items={[{ key: 'month', label: '按月' }, { key: 'year', label: '按年' }]} />
      <div className={s.stepper}>
        <button onClick={() => step(-1)} aria-label="上一期"><Icon name="back" /></button>
        <b className="tnum">{periodLabel}</b>
        <button onClick={() => step(1)} disabled={isCurrent} aria-label="下一期"><Icon name="chev" /></button>
      </div>
      <label className={s.sel}>
        <span>周转</span>
        <select value={days} onChange={(e) => set({ days: e.target.value === '30' ? undefined : e.target.value })}>
          {[30, 60, 90].map((d) => <option key={d} value={d}>近 {d} 天</option>)}
        </select>
      </label>
      <label className={s.sel}>
        <span>销售按</span>
        <select value={sb} onChange={(e) => set({ sb: e.target.value === 'createdAt' ? undefined : e.target.value })}>
          {salesBasis.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
        </select>
      </label>
      <label className={s.sel}>
        <span>采购按</span>
        <select value={pb} onChange={(e) => set({ pb: e.target.value === 'orderDate' ? undefined : e.target.value })}>
          {purchaseBasis.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
        </select>
      </label>
    </div>
  );

  const d = res.data;
  const t = d?.totals;
  const body = (() => {
    if (res.error) return <ErrorState title="经营报表加载失败" message={res.error.message} detail="GET /api/reports/business" onRetry={res.reload} />;
    if (!d || !t) return <div className={s.pad}><Skeleton /><Skeleton lines={6} /></div>;
    return (
      <div className={`${s.body} ${res.loading ? s.stale : ''}`}>
        <Tiles>
          <Tile label="期间销售额" value={money(t.salesAmount)} sub={`${salesBasis.find((x) => x.value === sb)?.label}口径`} />
          <Tile label="期间采购额" value={money(t.purchaseAmount)} sub={`${purchaseBasis.find((x) => x.value === pb)?.label}口径`} />
          <Tile label="应收余额" value={money(t.receivableBalance)} sub={<>其中逾期 <b className={t.overdueReceivable > 0 ? s.bad : undefined}>{money(t.overdueReceivable)}</b></>} />
          <Tile label="应付余额" value={money(t.payableBalance)} sub={<>其中逾期 <b className={t.overduePayable > 0 ? s.bad : undefined}>{money(t.overduePayable)}</b></>} />
          <Tile label="库存价值" value={money(t.inventoryValue)} sub="按采购价 × 现有库存" />
          <Tile label="周转偏慢 SKU" value={t.slowMovingCount} tone={t.slowMovingCount > 0 ? 'warn' : undefined} sub={`含无出库 · 共 ${d.inventoryTurnover.length} 个 SKU`} />
        </Tiles>
        <div className={s.two}>
          <AgingBars title="应收账龄" rows={d.receivableAging} asOf={d.filters.agingAsOf} />
          <AgingBars title="应付账龄" rows={d.payableAging} asOf={d.filters.agingAsOf} />
        </div>
        <Ranking title="客户排行" rows={d.customerRanking} kind="customer" phone={phone} />
        <Ranking title="供应商排行" rows={d.supplierRanking} kind="supplier" phone={phone} />
        <Turnover rows={d.inventoryTurnover} days={d.filters.inventoryDays} phone={phone} filter={invFilter} onFilter={(k) => set({ inv: k === 'all' ? undefined : k })} />
        <p className={s.note}>口径与老版「经营报表」一致，数字由服务端计算。</p>
      </div>
    );
  })();

  if (phone) {
    return (
      <div className={s.phone}>
        <PhoneBar large back="/apps" backLabel="应用" title="经营报表" sub={`${periodLabel} · 账龄截至今天`} />
        <div className={s.pctl}>{controls}</div>
        <div className={s.pscroll}>{body}</div>
      </div>
    );
  }
  return (
    <div className={s.desk}>
      <DeskHead title="经营报表" sub="库存周转、应收应付账龄、客户和供应商排行">{controls}</DeskHead>
      <div className={s.scroll}>{body}</div>
    </div>
  );
}
